import { useEffect, useMemo, useRef, useState } from "react";
import { Search, Loader2 } from "lucide-react";
import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { Input } from "@/components/ui/input";

export interface SelectedPlace {
  lat: number;
  lng: number;
  displayName: string;
  address: string;
  city: string;
  district: string;
  pincode: string;
}

interface SearchBoxProps {
  onSelect: (place: SelectedPlace) => void;
}

interface Prediction {
  place_id: string;
  description: string;
}

// Kerala bounding box
const KERALA_BOUNDS = { south: 8.17, north: 12.78, west: 74.85, east: 77.42 };

export const SearchBox = ({ onSelect }: SearchBoxProps) => {
  const placesLib = useMapsLibrary("places");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Prediction[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const sessionTokenRef =
    useRef<google.maps.places.AutocompleteSessionToken | null>(null);

  const autocomplete = useMemo(() => {
    if (!placesLib) return null;
    return new placesLib.AutocompleteService();
  }, [placesLib]);

  useEffect(() => {
    if (!autocomplete || query.trim().length < 3) {
      setResults([]);
      return;
    }
    if (!sessionTokenRef.current && placesLib) {
      sessionTokenRef.current = new placesLib.AutocompleteSessionToken();
    }
    setLoading(true);
    const handle = setTimeout(() => {
      autocomplete.getPlacePredictions(
        {
          input: query,
          sessionToken: sessionTokenRef.current ?? undefined,
          componentRestrictions: { country: "in" },
          locationBias: KERALA_BOUNDS,
        },
        (preds) => {
          setLoading(false);
          setResults(
            (preds ?? []).map((p) => ({
              place_id: p.place_id,
              description: p.description,
            })),
          );
          setOpen(true);
        },
      );
    }, 300);
    return () => clearTimeout(handle);
  }, [query, autocomplete, placesLib]);

  const choose = async (pred: Prediction) => {
    setOpen(false);
    setResults([]);
    if (!placesLib) return;
    try {
      const place = new placesLib.Place({ id: pred.place_id });
      await place.fetchFields({
        fields: [
          "location",
          "formattedAddress",
          "displayName",
          "addressComponents",
        ],
      });

      if (!place.location) return;

      const comps = place.addressComponents ?? [];
      const get = (type: string) =>
        comps.find((c) => c.types.includes(type))?.longText ?? "";
      const city = get("locality") || get("administrative_area_level_2");
      const district =
        get("administrative_area_level_3") ||
        get("administrative_area_level_2");
      const pincode = get("postal_code");
      const address =
        place.formattedAddress ?? place.displayName ?? pred.description;

      onSelect({
        lat: place.location.lat(),
        lng: place.location.lng(),
        displayName: address,
        address,
        city,
        district,
        pincode,
      });
      setQuery(address);
      setOpen(false);
      setResults([]);
      sessionTokenRef.current = null;
    } catch {
      // place fetch failed — leave UI state unchanged
    }
  };

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results.length && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder="Search address, landmark, or area…"
          className="pl-8 pr-8"
          disabled={!placesLib}
        />
        {loading && (
          <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground animate-spin" />
        )}
      </div>
      {open && results.length > 0 && (
        <ul className="absolute z-[1000] mt-1 w-full bg-card border border-border rounded-card shadow-lg max-h-72 overflow-auto">
          {results.map((r) => (
            <li key={r.place_id}>
              <button
                type="button"
                onClick={() => choose(r)}
                className="w-full text-left px-3 py-2 text-sm hover:bg-muted/50"
              >
                {r.description}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SearchBox;
